// React and core hooks
import { useContext, useEffect, useState } from "react";

// React Router
import { useNavigate } from "react-router-dom";

//React i18 translation
import { useTranslation } from "react-i18next";

// MUI components and icons
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Project-specific imports
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import { httpGet, httpPost } from "../../services/commonService";
import DataCardGrid from "../../shared-components/DataCardGrid/DataCardGrid";

const AffiliateProjectList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const [isAddLeadPopupOpen, setAddLeadPopupOpen] = useState(false);
  const [projectList, setProjectList] = useState([]);

  useEffect(() => {
    if (projectList.length === 0) {
      getBusinessTypes();
    }
  }, [projectList]);

  const handleClosePopup = () => {
    setAddLeadPopupOpen(false);
  };

  const columns = [
    {
      key: "LeadCollectionID",
      label: t("LeadCollection.LeadIdLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "ProjectID",
      label: t("LeadCollection.ProjectId"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "StatusUpdatedDateTimeWithTimeZone",
      label: t("LeadCollection.StatusUpdatedDate"),
      type: "date",
      isFilterVisible: true,
    },
    {
      key: "LeadStatus",
      label: t("LeadCollection.LeadStatus"),
      type: "text",
      isFilterVisible: true,
    },

    {
      key: "FirstName",
      label: t("LeadCollection.ListFirstNameLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "LastName",
      label: t("LeadCollection.ListLastNameLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "Email",
      label: t("LeadCollection.ListEmailLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "PhoneNumber",
      label: t("LeadCollection.ListPhoneLable"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "LeadFrom",
      label: t("LeadCollection.LeadFromLable"),
      type: "text",
    },
  ];

  const getBusinessTypes = async () => {
    await httpGet(
      urlConstant.Common.GetBusinessTypeDropDownList,
      true,
      false,
      accessToken,
      impersonateEmail
    )
      .then((response) => {
        setProjectList(response.Value.Data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchDataFunction = async ({
    page,
    pageSize,
    search,
    filters,
    sortKey,
    sortDirection,
  }) => {
    const pagingInfo = {
      PageNumber: page,
      RowsPerPage: pageSize,
      CommonSearchText: search,
      SortColumns: {
        SortColumnName: sortKey,
        SortOrder: sortDirection,
      },
      Filter: {
        LeadCollectionIDFilter: filters?.LeadCollectionID,
        FirstNameFilter: filters?.FirstName,
        LastNameFilter: filters?.LastName,
        EmailFilter: filters?.Email,
        PhoneNumberFilter: filters?.PhoneNumber,
        LeadStatusFilter: filters?.LeadStatus,
        LeadProjectIDFilter: filters?.ProjectID,
        LeadProjectStatusFilter: filters?.ProjectStatus,
        StatusUpdatedDateTimeFilter: filters?.StatusUpdatedDateTimeWithTimeZone,
      },
    };
    const requestBody = {
      PagingInfoVM: pagingInfo,
    };
    try {
      const response = await httpPost(
        urlConstant.LeadCollections.GetLeadCollectionListByUserId,
        requestBody,
        true,
        false,
        accessToken,
        impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        return {
          data: response.Value.Data.Data,
          totalCount: response.Value.Data.TotalCount,
        };
      } else {
        return { data: [], totalCount: 0 };
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"} width={"100%"}>
        <Typography
          color="secondary.light"
          display={{ xs: "flex", sm: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
        >
          {t("ProjectsPage.Affiliate Leads")}
        </Typography>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            border: "1px solid var(--headerbg)",
            borderRadius: "20px",
            height: "100%",
          })}
        >
          <Stack gap={"20px"}>
            <Stack
              direction={"row"}
              gap={1}
              width={"100%"}
              alignItems={"center"}
              justifyContent={"space-between"}
              flexWrap={"wrap"}
            >
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("ProjectsPage.AffiliateLeadList")}
              </Typography>
              <Button
                variant="contained"
                sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                onClick={() => setAddLeadPopupOpen(true)}
              >
                {t("LeadCollection.AddLead")}
              </Button>
            </Stack>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <DataCardGrid
                  onFetchData={fetchDataFunction}
                  columns={columns}
                  defaultItemsPerPage={8}
                  defaultColumnsPerGrid={3}
                  enableExport={true}
                  enablePagination={true}
                  uniqueId="affiliateprojects-grid"
                />
              </Grid>
            </Grid>
          </Stack>
        </Card>
      </Box>

      {/* Add Lead Popup - Choose Business Type */}
      <Dialog
        open={isAddLeadPopupOpen}
        aria-labelledby="customized-dialog-title"
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {t("LeadCollection.AddLead")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClosePopup}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={(theme) => ({
            background: theme.palette.background.cardGradient,
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          })}
          dividers
        >
          <Button
            variant="contained"
            sx={{ px: 0.8, py: 0.5, width: "auto", backgroundColor: "#FF6C6C" }}
            color="primary"
            disableElevation
          >
            <img src="/icons/user-header.svg" alt="user" />
          </Button>
          <Stack direction={"column"} alignItems={"center"}>
            <Typography
              color="secondary.text"
              sx={{ fontSize: "12px" }}
              variant="body2"
            >
              {t("LeadCollection.LeadFor")}
            </Typography>
          </Stack>
          <Stack direction={"row"} gap={"20px"} flexWrap="wrap">
            {projectList
              .filter((item) => !item.isDisabled)
              .map((item, index) => {
                return (
                  <Button
                    variant="contained"
                    sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                    key={index}
                    onClick={() =>
                      navigate("/manageAffiliateProjects", {
                        state: { businessType: item.label },
                      })
                    }
                  >
                    {item.label}
                  </Button>
                );
              })}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AffiliateProjectList;
